import { useCallback, useMemo } from "react";

import { isEmpty } from "lodash";

import { translate } from "app/filters/i18nFilter";
import { ComboboxWithSelectAll } from "app/pages/sales-intelligence/common-components/dropdown/ComboboxWithSelectAll";
import type RoleChangeType from "app/pages/sales-intelligence/sub-modules/signals/models/role-changes/RoleChangeType";
import {
    getRoleChangeTypeDescriptionKey,
    getRoleChangeTypeLabelKey,
} from "app/pages/sales-intelligence/sub-modules/signals/utils/role-changes/roleChangeTypeI18n";

import useMultiselectHandler from "../../../hooks/useMultiselectHandler";
import useSignalFilterDropdownLabel from "../../../hooks/useSignalFilterDropdownLabel";
import { FilterType } from "../../../models/services/FilterService";
import filterService from "../../../services/filters/filterService";
import FilterButtonLabel from "../../filters/shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "../shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import { DROPDOWN_DEFAULT_WIDTH } from "../shared/FilterMultiselectDropdown/const";
import SignalFilterDropdownLabel from "../shared/SignalFilterDropdownLabel/SignalFilterDropdownLabel";
import { StyledSignalFilterBlock } from "../shared/styles";

import { RoleChangeTypeOptionContent } from "./RoleChangeTypeDropdownItem";

type FilterValue = ReadonlyArray<RoleChangeType> | undefined;

type RoleChangeTypeItem = {
    id: RoleChangeType;
    text: string;
};

export type SignalRoleChangeTypesFilterProps = {
    filterKey: string;
    selectedValue: FilterValue;
    options: ReadonlyArray<RoleChangeType>;
    onValueChange(filterValue: FilterValue): void;
    autoOpen?: boolean;
};

const SignalRoleChangeTypesFilter = ({
    filterKey,
    selectedValue,
    options,
    onValueChange,
    autoOpen,
}: SignalRoleChangeTypesFilterProps) => {
    const comparableSelectedValue = useMemo(() => selectedValue ?? [], [selectedValue]);
    const isFullSelection = options.length > 0 && options.length === comparableSelectedValue.length;
    const isPartiallySelected = !isEmpty(comparableSelectedValue) && !isFullSelection;

    const items = useMemo(
        (): readonly RoleChangeTypeItem[] =>
            options.map((changeType) => ({
                id: changeType,
                text: `${translate(getRoleChangeTypeLabelKey(changeType))} ${translate(getRoleChangeTypeDescriptionKey(changeType))}`,
            })),
        [options],
    );

    const buildFilterName = useCallback(() => {
        const nameBuilder = filterService.getFilterName(FilterType.RoleChangeTypes);

        return nameBuilder.buildName({ changeTypes: comparableSelectedValue }, options);
    }, [comparableSelectedValue, options]);

    const { model: filterLabelModel } = useSignalFilterDropdownLabel({ buildName: buildFilterName });

    const { onSelect } = useMultiselectHandler({
        selectedValue,
        onValueChange,
    });

    const handleSelectAll = useCallback(() => {
        onValueChange(isFullSelection ? [] : [...options]);
    }, [isFullSelection, onValueChange, options]);

    const handleSelect = useCallback(
        (item: RoleChangeTypeItem) => {
            const changeType = item.id;
            const isSelected = comparableSelectedValue.includes(changeType);

            onSelect(changeType, isSelected);
        },
        [comparableSelectedValue, onSelect],
    );

    const renderButton = ({ isOpen, disabled }: { isOpen: boolean; disabled?: boolean }) => {
        const isEmptySelection = isEmpty(selectedValue);

        return (
            <SignalFilterDropdownButton
                id={filterKey}
                isOpen={isOpen}
                disabled={disabled}
                hasValue={!isEmptySelection}
            >
                <FilterButtonLabel>
                    <SignalFilterDropdownLabel model={filterLabelModel} />
                </FilterButtonLabel>
            </SignalFilterDropdownButton>
        );
    };

    return (
        <StyledSignalFilterBlock id={filterKey}>
            <ComboboxWithSelectAll
                items={items}
                isFullSelection={isFullSelection}
                isPartiallySelected={isPartiallySelected}
                isItemSelected={(item) => comparableSelectedValue.includes(item.id)}
                selectAllLabel={translate(`si.signals.filters.${filterKey}.name.all`, { count: options.length })}
                hasSearch
                autoOpen={autoOpen}
                isTopLayer
                width={DROPDOWN_DEFAULT_WIDTH}
                searchPlaceHolder={translate(`si.signals.filters.${filterKey}.search.placeholder`)}
                dataAutomation={`signal-filter-combobox-${filterKey}`}
                onSelectAll={handleSelectAll}
                onSelect={handleSelect}
                renderButton={renderButton}
                renderOption={(item, isSelected) => (
                    <RoleChangeTypeOptionContent
                        label={translate(getRoleChangeTypeLabelKey(item.id))}
                        description={translate(getRoleChangeTypeDescriptionKey(item.id))}
                        isSelected={isSelected}
                    />
                )}
            />
        </StyledSignalFilterBlock>
    );
};

export default SignalRoleChangeTypesFilter;
